import { z } from 'zod';
/**
 * What the AI reads off a certificate — spec v4 §24.
 *
 * Upload and email arrive with a PDF and nothing else, so the reconciliation the check
 * normally performs has only one side. Extracting this payload gives the self-contained
 * rules something to run against, and gives the chamber the fields it needs for reporting,
 * billing and analytics.
 *
 * **This is never a declaration.** It is what a machine read off a page, with an error
 * rate. It is stored and displayed as `EXTRACTED`, never as `ASSERTED`, and must never be
 * presented to a customs authority as what the exporter declared.
 */
/** How sure the model is about one field. Absent means it could not read it at all. */
export const fieldConfidenceSchema = z.number().min(0).max(1);
export const extractedPartySchema = z.object({
    companyName: z.string().nullable(),
    abn: z.string().nullable(),
    addressLine1: z.string().nullable(),
    suburb: z.string().nullable(),
    state: z.string().nullable(),
    postcode: z.string().nullable(),
    country: z.string().nullable(),
});
export const extractedGoodsLineSchema = z.object({
    lineNumber: z.number().int().nullable(),
    description: z.string().nullable(),
    hsCode: z.string().nullable(),
    quantity: z.string().nullable(),
    unit: z.string().nullable(),
    grossWeight: z.string().nullable(),
    weightUnit: z.string().nullable(),
    value: z.string().nullable(),
    currency: z.string().nullable(),
    countryOfOrigin: z.string().nullable(),
    /** WO, PE, PSR, etc. Only meaningful on a preferential certificate. */
    originCriterion: z.string().nullable(),
    marksAndNumbers: z.string().nullable(),
});
export const extractedCertificateSchema = z.object({
    /**
     * Which kind of certificate this is.
     *
     * The most operationally important field in here: the stamping layout, the number format
     * and the accreditation all key off it, so getting it wrong produces a correctly-stamped
     * certificate of the wrong kind. It is therefore a **suggestion the operator confirms**,
     * never an automatic selection (spec v4 §26.3).
     */
    documentTypeCode: z.string().nullable(),
    documentTypeConfidence: fieldConfidenceSchema.nullable(),
    /** Why the model thinks so, in one sentence, for the operator deciding. */
    documentTypeReasoning: z.string().nullable(),
    exporter: extractedPartySchema,
    consignee: extractedPartySchema,
    /** Often the same as the consignee; separately stated on some FTA forms. */
    importer: extractedPartySchema.nullable(),
    invoiceNumber: z.string().nullable(),
    invoiceDate: z.string().nullable(),
    invoiceTotal: z.string().nullable(),
    currency: z.string().nullable(),
    destinationCountry: z.string().nullable(),
    portOfLoading: z.string().nullable(),
    portOfDischarge: z.string().nullable(),
    departureDate: z.string().nullable(),
    vesselOrFlight: z.string().nullable(),
    goodsLines: z.array(extractedGoodsLineSchema).max(200),
    /** Who signed the exporter's declaration — checked against the specimens on file. */
    declarationSignatoryName: z.string().nullable(),
    declarationSignatoryPosition: z.string().nullable(),
    declarationDate: z.string().nullable(),
    declarationPlace: z.string().nullable(),
    /** Per-field 0–1. A low score is a finding in its own right, not a silent absence. */
    confidence: z.record(fieldConfidenceSchema),
    /** Anything the model could not read, named so the operator knows to look. */
    unreadable: z.array(z.string()),
});
/** Where a document's structured data came from. */
export const DataProvenance = {
    /** The exporter or their software declared it. */
    ASSERTED: 'ASSERTED',
    /** A machine read it off the page. */
    EXTRACTED: 'EXTRACTED',
};
export const extractionStateSchema = z.enum(['PENDING', 'COMPLETE', 'UNAVAILABLE']);
/** What the console shows on the review screen. */
export const extractionPanelSchema = z.object({
    state: extractionStateSchema,
    provenance: z.enum(['ASSERTED', 'EXTRACTED']),
    /** Set when extraction could not run — stated plainly, never as a silent empty result. */
    unavailableReason: z.string().nullable(),
    model: z.string().nullable(),
    extractedAt: z.string().nullable(),
    data: extractedCertificateSchema.nullable(),
    /** The suggested type resolved against this chamber's accreditations. */
    suggestedDocumentTypeId: z.string().nullable(),
    suggestedDocumentTypeName: z.string().nullable(),
    /** True when the suggestion disagrees with the type currently on the document. */
    suggestionDiffers: z.boolean(),
});
/** What each extracted field is called on screen. Operators never see a camelCase name. */
const FIELD_LABELS = {
    exporter: 'exporter',
    exporterCompanyName: "exporter's name",
    exporterAbn: "exporter's ABN",
    consignee: 'consignee',
    consigneeCompanyName: "consignee's name",
    consigneeAbn: "consignee's ABN",
    importer: 'importer',
    importerAbn: "importer's ABN",
    invoiceNumber: 'invoice number',
    invoiceDate: 'invoice date',
    invoiceTotal: 'invoice total',
    currency: 'currency',
    destinationCountry: 'destination',
    portOfLoading: 'port of loading',
    portOfDischarge: 'port of discharge',
    departureDate: 'departure date',
    vesselOrFlight: 'vessel or flight',
    goodsLines: 'goods lines',
    declarationSignatoryName: "signatory's name",
    declarationSignatoryPosition: "signatory's position",
    declarationDate: 'declaration date',
    declarationPlace: 'place of declaration',
};
/** "invoice total, currency and consignee" from ['invoiceTotal', 'currency', 'consignee']. */
export function describeFields(names) {
    const labels = names.map((name) => {
        // The reader names some fields as paths ("exporter.abn"); "exporterAbn" is the same field.
        const key = name.replace(/\.(\w)/g, (_, c) => c.toUpperCase());
        return FIELD_LABELS[key] ?? key.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
    });
    if (labels.length <= 1)
        return labels.join('');
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
//# sourceMappingURL=extraction.js.map